import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useDeleteBackgroundJobMutation, useGetBackgroundJobsQuery } from '../redux/services/record';
import { apiTypeToJobType, hydrateJobs, selectSubmittedJobs } from '../redux/slices/background-job-slice';
import { apiJobTypes, type BackgroundJobsItem } from '../@types/background-jobs';

// Job types that should be hydrated on page load
const hydrateableJobTypes = Object.keys(apiTypeToJobType);

function isStagedAttachmentImportJob(job: BackgroundJobsItem): boolean {
  return job.type === apiJobTypes.ATTACHMENT_IMPORT && job.status === 'STAGED';
}

/**
 * Hook to hydrate background jobs from the API into the Redux store on page load.
 *
 * This ensures that jobs started before a page refresh continue to be tracked and polled.
 */
export const useHydrateBackgroundJobs = () => {
  const dispatch = useAppDispatch();
  const hasHydrated = useRef(false);
  const submittedJobs = useAppSelector(selectSubmittedJobs);
  const [deleteBackgroundJob] = useDeleteBackgroundJobMutation();

  // Fetch jobs from API
  const { data, isSuccess } = useGetBackgroundJobsQuery(
    {
      page: 1,
      pageSize: 200,
      type: hydrateableJobTypes.join(','),
    },
    { skip: hasHydrated.current }
  );

  useEffect(() => {
    if (isSuccess && data?.items && !hasHydrated.current) {
      hasHydrated.current = true;

      const existingIds = new Set(submittedJobs.map((s) => (s.details as Record<string, unknown>)?.backgroundJobId));
      const hydrateableJobs = data.items.filter((job) => !existingIds.has(job.id) && !isStagedAttachmentImportJob(job));

      if (hydrateableJobs.length > 0) {
        dispatch(hydrateJobs(hydrateableJobs));
      }

      // If there are any staged attachment import jobs, delete them as they are likely stuck due to the file upload being done but the job not starting
      // Fire-and-forget: run in background, silently ignore errors
      const stagedAttachmentImportJobs = data.items.filter(isStagedAttachmentImportJob);
      stagedAttachmentImportJobs.forEach((job) => deleteBackgroundJob(job.id).catch(() => {}));
    }
  }, [isSuccess, data, submittedJobs, dispatch, deleteBackgroundJob]);
};
